import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  CONTACT_EMAIL, CORAL, DEFAULT_SLUG, DayStrip, MUTED, NAVY, Notice, PrimaryButton,
  SectionHeading, Shell, SlotGrid, TimezoneChoice, linkButtonStyle,
} from '../components/booking-ui';
import { groupByLocalDay } from '../lib/group-days';
import { BRISBANE, detectTimezone, formatLong, formatTime, readsAsBrisbane } from '../lib/timezones';
import {
  brisbaneToday, useAvailability, useBookingRef, useReschedule, type AvailabilitySlot,
} from '../hooks/use-public-booking';

/**
 * One booking, at a URL the visitor can come back to.
 *
 * Before this, confirming a booking showed a success state that lived in React memory
 * and died with the tab. There was nothing to bookmark, nothing to forward to a
 * colleague, and nothing for "change the time" in the email to point at that was not a
 * fresh booking form.
 *
 * THE TOKEN IS A CAPABILITY, and this page reads the booking with it, which the cancel
 * page deliberately does not. That is a considered difference rather than an
 * inconsistency. Cancelling is destructive and needs no context to perform, so the
 * cancel page shows nothing before the visitor commits. A reference page has to show the
 * booking to be a page at all, so the protection lives in what the API returns instead:
 * no attribution, no campaign codes, no party id, no internal ids. Everything on screen
 * here is something the holder of the link was already emailed.
 *
 * CHANGING THE TIME MOVES THIS BOOKING. It does not make a second one. That is the whole
 * point of the route behind it.
 */

const COPY = {
  notFound: "We can't find that booking. It may already have been cancelled, or the link may have been cut short by an email client.",
  cancelled: 'This one is cancelled and the time is free again. You can book another whenever it suits.',
  moveFailed: 'That did not go through. Give it a moment and try again.',
  noSlots: `Nothing free in the next fortnight. Email ${CONTACT_EMAIL} and we'll find a time.`,
  calendarDown: "The calendar isn't answering right now. Give it a minute and refresh.",
} as const;

export default function BookRefPage() {
  const { token } = useParams<{ token: string }>();
  const booking = useBookingRef(token);

  const visitorZone = useMemo(() => detectTimezone(), []);
  const [zone, setZone] = useState(visitorZone);
  const [picking, setPicking] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);

  const from = useMemo(() => brisbaneToday(), []);
  const slug = booking.data?.slug;
  // Only asked for once the visitor says they want to move it. Somebody opening their
  // confirmation to check a time should not cost a free-busy read of the host's diary.
  const availability = useAvailability(picking ? slug : undefined, from);
  const refetchAvailability = availability.refetch;
  const onSlotConflict = useCallback(() => refetchAvailability(), [refetchAvailability]);
  const move = useReschedule(token, onSlotConflict);

  const days = availability.data?.days;
  const daysWithSlots = useMemo(() => groupByLocalDay(days, zone), [days, zone]);

  useEffect(() => {
    if (!daysWithSlots.length) return;
    if (!selectedDate || !daysWithSlots.some((d) => d.date === selectedDate)) {
      setSelectedDate(daysWithSlots[0].date);
    }
  }, [daysWithSlots, selectedDate]);

  useEffect(() => {
    if (!selectedSlot || !days) return;
    const live = days.some((d) => d.slots.some((s) => s.token === selectedSlot.token));
    if (!live) setSelectedSlot(null);
  }, [days, selectedSlot]);

  // A completed move folds the picker away and leaves the new time on screen. The
  // response is the booking, so nothing has to be refetched to show it.
  useEffect(() => {
    if (move.isSuccess) {
      setPicking(false);
      setSelectedSlot(null);
    }
  }, [move.isSuccess]);

  if (booking.isLoading) {
    return <Shell><p style={{ color: MUTED }}>Finding your booking…</p></Shell>;
  }

  if (booking.isError) {
    return (
      <Shell>
        <Notice
          heading="We can't find that booking"
          message={booking.error?.status === 404 ? COPY.notFound : (booking.error?.message ?? COPY.notFound)}
          action={<a href={`/book/${DEFAULT_SLUG}/`} style={linkButtonStyle}>Book a time</a>}
        />
      </Shell>
    );
  }

  // The move returns the booking in its new shape, so it wins over what was first read.
  const current = move.data ?? booking.data!;

  if (current.status === 'cancelled') {
    return (
      <Shell title={current.title} photoUrl={current.host_photo_url}>
        <p style={{ margin: '0 0 8px', fontSize: 15, color: MUTED }}>Cancelled</p>
        <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.6 }}>{COPY.cancelled}</p>
        <p style={{ margin: '0 0 20px', fontSize: 15, color: MUTED, textDecoration: 'line-through' }}>
          {formatLong(current.starts_at, zone)}
        </p>
        <a href={`/book/${current.slug}/`} style={linkButtonStyle}>Book another time</a>
      </Shell>
    );
  }

  const showZoneChoice = !readsAsBrisbane(current.starts_at, visitorZone);
  const activeDay = daysWithSlots.find((d) => d.date === selectedDate) ?? null;
  const moveError = move.isError
    ? (move.error instanceof ApiError ? move.error.message : COPY.moveFailed)
    : null;

  return (
    <Shell title={current.title} photoUrl={current.host_photo_url}>
      <p style={{ margin: '-8px 0 6px', fontSize: 15, color: MUTED }}>
        {`${current.duration_mins} minutes`}
      </p>

      {showZoneChoice
        ? <TimezoneChoice zone={zone} visitorZone={visitorZone} onChange={setZone} />
        : <p style={{ margin: '0 0 20px', fontSize: 14, color: MUTED }}>Times shown in Brisbane time (AEST).</p>}

      {/* Two columns only while there is a second column to fill. Viewing a booking is
          one short list of facts, and putting it in a half-width column beside nothing
          reads as a page that failed to load its other half. */}
      <div className={picking ? 'booking-layout' : undefined}>
        <div className="booking-layout__context">
          <div style={{ marginBottom: 18 }}>
            <p style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 600, color: NAVY }}>
              {formatLong(current.starts_at, zone)}
            </p>
            {/* The meeting happens in Brisbane, so the visitor's clock never stands on
                its own. Somebody reading Perth time still needs the number the host
                will say back to them on the phone. */}
            {!readsAsBrisbane(current.starts_at, zone) ? (
              <p style={{ margin: 0, fontSize: 14, color: MUTED }}>
                {`That is ${formatLong(current.starts_at, BRISBANE)} in Brisbane.`}
              </p>
            ) : null}
          </div>

          {move.isSuccess && move.data?.moved ? (
            <p role="status" style={{ margin: '0 0 18px', fontSize: 15, lineHeight: 1.6 }}>
              That is moved. Outlook will update the invite you already have.
            </p>
          ) : null}

          {current.teams_join_url ? (
            <a href={current.teams_join_url} style={{ ...linkButtonStyle, marginBottom: 20 }}>
              Join the Teams meeting
            </a>
          ) : (
            <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.6 }}>
              Todd will send the meeting link through before then.
            </p>
          )}

          <p style={{ margin: '0 0 6px', fontSize: 14, color: MUTED }}>
            {`This is booked for ${current.name?.trim() || current.email}.`}
          </p>
          <p style={{ margin: '0 0 20px', fontSize: 14, color: MUTED }}>
            Your calendar invite is in your inbox, along with a confirmation.
          </p>

          {!picking ? (
            <>
              <PrimaryButton onClick={() => setPicking(true)} style={{ marginBottom: 12 }}>
                Change the time
              </PrimaryButton>
              <p style={{ margin: 0, fontSize: 14, color: MUTED }}>
                {'If the plan has changed altogether, you can '}
                <a href={`/book/cancel/${token}/`} style={{ color: NAVY }}>cancel this booking</a>
                {' instead.'}
              </p>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setPicking(false); setSelectedSlot(null); }}
              style={{
                border: 'none', background: 'none', padding: 0, font: 'inherit',
                color: NAVY, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer',
                // Clears the picker heading that follows it in the single-column stack.
                display: 'block', marginBottom: 24,
              }}
            >
              Keep the time I have
            </button>
          )}
        </div>

        {picking ? (
          <div className="booking-layout__calendar">
            <SectionHeading>Pick a new time</SectionHeading>

            {availability.isLoading ? <p style={{ color: MUTED }}>Checking what's free…</p> : null}

            {availability.isError ? (
              <Notice
                heading="The calendar isn't answering"
                message={COPY.calendarDown}
                action={
                  <PrimaryButton onClick={() => availability.refetch()} style={{ maxWidth: 220, margin: '0 auto' }}>
                    Try again
                  </PrimaryButton>
                }
              />
            ) : null}

            {availability.isSuccess && daysWithSlots.length === 0 ? (
              <Notice
                heading="Nothing free just now"
                message={COPY.noSlots}
                action={<a href={`mailto:${CONTACT_EMAIL}`} style={{ color: NAVY, fontWeight: 600 }}>Email us</a>}
              />
            ) : null}

            {availability.isSuccess && daysWithSlots.length > 0 ? (
              <>
                <DayStrip
                  days={daysWithSlots}
                  selected={selectedDate}
                  zone={zone}
                  onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                />
                <SlotGrid day={activeDay} selected={selectedSlot} zone={zone} onSelect={setSelectedSlot} />

                {moveError ? (
                  <p role="alert" style={{ margin: '16px 0 0', fontSize: 15, lineHeight: 1.6, color: CORAL }}>
                    {moveError}
                  </p>
                ) : null}

                <PrimaryButton
                  type="button"
                  disabled={!selectedSlot || move.isPending}
                  onClick={() => selectedSlot && move.mutate({ token: selectedSlot.token })}
                  style={{ marginTop: 16 }}
                >
                  {move.isPending
                    ? 'Moving…'
                    : selectedSlot
                      ? `Move to ${formatTime(selectedSlot.starts_at, zone)}`
                      : 'Pick a time first'}
                </PrimaryButton>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </Shell>
  );
}
